/**
 * PAPPO — ResetPasswordPage (/reset-password?token=...)
 * Redefinição de senha a partir do link enviado por email. Lê o token da URL,
 * pede a nova senha (com confirmação) e chama o backend. Reaproveita o visual
 * da ForgotPasswordPage.
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { resetPassword } from '../services/auth';
import styles from './ForgotPasswordPage.module.css';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [erro, setErro] = useState('');
  const [done, setDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErro('');

    if (password.length < 6) {
      setErro('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setErro('As senhas não coincidem.');
      return;
    }

    setIsLoading(true);
    const res = await resetPassword(token, password);
    setIsLoading(false);

    if (res.success) {
      setDone(true);
      // Leva ao login após um instante.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } else {
      setErro(res.error ?? 'Não foi possível redefinir a senha.');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <h1 className={styles.title}>Nova senha</h1>
          <p className={styles.subtitle}>Crie uma nova senha para sua conta</p>
        </div>

        {!token ? (
          <div className={styles.success}>
            <p>Link inválido. Abra o link que enviamos no seu email.</p>
          </div>
        ) : done ? (
          <div className={styles.success}>
            <p>
              <CheckCircle2 size={18} style={{ verticalAlign: 'middle' }} /> Senha
              redefinida! Redirecionando para o login…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <Input
              label="Nova senha"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock size={16} />}
              autoComplete="new-password"
            />
            <Input
              label="Confirmar nova senha"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              leftIcon={<Lock size={16} />}
              autoComplete="new-password"
            />
            {erro && (
              <div className={styles.error} role="alert">
                {erro}
              </div>
            )}
            <Button type="submit" fullWidth isLoading={isLoading} size="lg">
              Redefinir senha
            </Button>
          </form>
        )}

        <div className={styles.links}>
          <Link to="/login" className={styles.backLink}>
            <ArrowLeft size={16} />
            Voltar ao login
          </Link>
        </div>
      </div>
    </div>
  );
}
